/**
 * Per-row action menu for the users table.
 *
 * Extracted from UserManagementPage.tsx (Sub-PR 6.3).
 */

import { useTranslation } from 'react-i18next'
import { Edit, Trash2, Shield, ShieldOff, KeyRound } from 'lucide-react'
import type { User } from '../../types'

interface UserActionMenuProps {
  user: User
  isPlatformAdmin: boolean
  onEdit: (user: User) => void
  onToggleStatus: (user: User) => void
  onResetPassword: (user: User) => void
  onDelete: (user: User) => void
}

export function UserActionMenu({
  user,
  isPlatformAdmin,
  onEdit,
  onToggleStatus,
  onResetPassword,
  onDelete,
}: UserActionMenuProps): React.ReactElement {
  const { t } = useTranslation('admin')

  return (
    <div className="absolute right-0 z-10 mt-1 w-48 rounded-lg border border-stone-200 bg-white shadow-lg dark:border-stone-700 dark:bg-stone-800">
      <button
        onClick={() => {
          onEdit(user)
        }}
        className="flex w-full items-center gap-2 px-4 py-2 text-sm text-stone-700 hover:bg-stone-50 dark:text-stone-300 dark:hover:bg-stone-700"
      >
        <Edit className="h-4 w-4" />
        {t('actions.edit')}
      </button>
      <button
        onClick={() => {
          onToggleStatus(user)
        }}
        className="flex w-full items-center gap-2 px-4 py-2 text-sm text-stone-700 hover:bg-stone-50 dark:text-stone-300 dark:hover:bg-stone-700"
      >
        <ToggleStatusLabel status={user.status} />
      </button>
      <button
        onClick={() => {
          onResetPassword(user)
        }}
        className="flex w-full items-center gap-2 px-4 py-2 text-sm text-stone-700 hover:bg-stone-50 dark:text-stone-300 dark:hover:bg-stone-700"
      >
        <KeyRound className="h-4 w-4" />
        {t('users.actions.resetPassword')}
      </button>
      {isPlatformAdmin && (
        <button
          onClick={() => {
            onDelete(user)
          }}
          className="flex w-full items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
        >
          <Trash2 className="h-4 w-4" />
          {t('actions.delete')}
        </button>
      )}
    </div>
  )
}

function ToggleStatusLabel({ status }: { status: string }): React.ReactElement {
  const { t } = useTranslation('admin')
  if (status === 'active') {
    return (
      <>
        <ShieldOff className="h-4 w-4" />
        {t('users.actions.suspend')}
      </>
    )
  }
  if (status === 'suspended') {
    return (
      <>
        <Shield className="h-4 w-4" />
        {t('users.actions.reactivate')}
      </>
    )
  }
  return (
    <>
      <Shield className="h-4 w-4" />
      {t('users.actions.activate')}
    </>
  )
}
